const EVENTS = window.theme.results.events

const SORT_OPTIONS = [
  { field: 'score', order: 'desc', label: 'Relevance', selected: true },
  {
    field: 'price',
    order: 'asc',
    label: 'Price: Low to High',
    selected: false,
  },
  {
    field: 'price',
    order: 'desc',
    label: 'Price: High to Low',
    selected: false,
  },
  { field: 'name', order: 'asc', label: 'Name: A to Z', selected: false },
  { field: 'name', order: 'desc', label: 'Name: Z to A', selected: false },
  { field: 'datePublished', order: 'desc', label: 'Newest', selected: false },
  { field: 'datePublished', order: 'asc', label: 'Oldest', selected: false },
]

class ResultsStateLayer {
  constructor () {
    this.eventBus = window.theme.results.eventBus

    this.state = {
      activeTerm: this.activeTerm,
      activeFacets: [],
      activeSort: { field: 'score', order: 'desc' },
      facets: [],
      pagination: {},
      products: [],
      productsPerPage: 24,
      sort: SORT_OPTIONS.map(option => ({ ...option })),
      lastInteraction: null,
    }

    this.subscribeToEvents()
    this.initializeStateFromUrl()

    queueMicrotask(() => {
      this.eventBus.publish(EVENTS.API.FETCH_PRODUCTS, {})
    })
  }

  subscribeToEvents () {
    this.eventBus.subscribe(EVENTS.API.PRODUCTS_FETCHED, response =>
      this.onProductsFetched(response),
    )
    this.eventBus.subscribe(EVENTS.API.FACETS_FETCHED, facets => this.updateFacets(facets))
    this.eventBus.subscribe(EVENTS.UI.FACET_CLEAR_ALL, () => this.onFacetClearAll())
    this.eventBus.subscribe(EVENTS.UI.SORT_CHANGED, sort => this.onSortChanged(sort))
    this.eventBus.subscribe(EVENTS.UI.PRICE_CHANGED, ({ min, max }) =>
      this.onPriceChanged({ min, max }),
    )
    this.eventBus.subscribe(EVENTS.UI.FACET_ADDED, ({ field, value }) =>
      this.onFacetAdded({ field, value }),
    )
    this.eventBus.subscribe(EVENTS.UI.FACET_REMOVED, ({ field, value }) =>
      this.onFacetRemoved({ field, value }),
    )
    this.eventBus.subscribe(EVENTS.STATE.ACTIVE_SORT_UPDATED, activeSort =>
      this.onActiveSortUpdated(activeSort),
    )
    this.eventBus.subscribe(EVENTS.UI.PAGINATION_CHANGED, cursor =>
      this.onPaginationChanged(cursor),
    )
    this.eventBus.subscribe(EVENTS.UI.PAGINATION_NEXT, () => this.onPaginationNext())
  }

  async onProductsFetched (response) {
    const pagination = {
      total: response.products.total,
      size: response.products.size,
      from: response.products.from,
      totalPages: Math.ceil(response.products.total / response.products.size),
      currentPage: Math.ceil((response.products.from + 1) / response.products.size),
      showNextButton: response.products.from + response.products.size < response.products.total,
      showPreviousButton: response.products.from > 0,
    }

    this.updatePagination({ ...this.state.pagination, ...pagination })

    let normalized = this.normalizeProducts(response.products.hits)

    if (window.Shopify.currency.active !== 'GBP') {
      normalized = await this.updateWithShopifyPrices(normalized)
    }

    this.updateProducts(normalized)
  }

  normalizeProducts (hits) {
    return hits.map(product => {
      let productHandle = ''
      if (product.url) {
        const urlMatch = product.url.match(/\/products\/([^/?#]+)/)
        productHandle = urlMatch ? urlMatch[1] : ''
      }

      return {
        ...product,
        productHandle,
      }
    })
  }

  async updateWithShopifyPrices (hits) {
    const results = await Promise.allSettled(
      hits.map(hit =>
        hit.productHandle
          ? window.BAOv2.utils.fetchProductData(hit.productHandle)
          : Promise.reject(new Error('missing handle')),
      ),
    )

    return hits.map((hit, i) => {
      const settled = results[i]
      if (settled.status !== 'fulfilled') return hit

      const data = settled.value?.product
      if (!data) return hit

      const priceCents = data.price ?? data.variants?.[0]?.price
      const compareCents = data.compare_at_price ?? data.variants?.[0]?.compare_at_price

      if (priceCents == null) return hit

      return {
        ...hit,
        price: priceCents,
        listPrice: compareCents,
      }
    })
  }

  onFacetClearAll () {
    this.state.lastInteraction = 'filter'
    this.state.pagination.currentPage = 1
    this.state.pagination.from = 0
    this.updateActiveFacets([])
    this.updateActiveSort({ field: 'score', order: 'desc' })
    this.syncUrlAndFetch()
  }

  onSortChanged ({ field, order }) {
    this.state.lastInteraction = 'sort'
    this.state.pagination.currentPage = 1
    this.state.pagination.from = 0
    this.updateActiveSort({ field, order })
    this.syncUrlAndFetch()
  }

  onPriceChanged ({ min, max }) {
    this.state.lastInteraction = 'filter'
    this.state.pagination.currentPage = 1
    this.state.pagination.from = 0
    const field = 'price'
    const value = [`${min}~${max}`]
    const existing = this.state.activeFacets.find(facet => facet.field === field)

    if (existing) {
      this.updateActiveFacets(
        this.state.activeFacets.map(facet => (facet.field === field ? { field, value } : facet)),
      )
    } else {
      this.updateActiveFacets([...this.state.activeFacets, { field, value }])
    }

    this.syncUrlAndFetch()
  }

  onFacetAdded ({ field, value }) {
    this.state.lastInteraction = 'filter'
    this.state.pagination.currentPage = 1
    this.state.pagination.from = 0
    const existing = this.state.activeFacets.find(facet => facet.field === field)

    if (existing) {
      const merged = [...new Set([...existing.value, ...value])]
      this.updateActiveFacets(
        this.state.activeFacets.map(facet =>
          facet.field === field ? { field, value: merged } : facet,
        ),
      )
    } else {
      this.updateActiveFacets([...this.state.activeFacets, { field, value }])
    }

    this.syncUrlAndFetch()
  }

  onFacetRemoved ({ field, value }) {
    this.state.lastInteraction = 'filter'
    this.state.pagination.currentPage = 1
    this.state.pagination.from = 0
    const existing = this.state.activeFacets.find(facet => facet.field === field)

    if (existing) {
      const filtered = existing.value.filter(v => !value.includes(v))
      if (filtered.length > 0) {
        this.updateActiveFacets(
          this.state.activeFacets.map(facet =>
            facet.field === field ? { field, value: filtered } : facet,
          ),
        )
      } else {
        this.updateActiveFacets(this.state.activeFacets.filter(facet => facet.field !== field))
      }
    }

    this.syncUrlAndFetch()
  }

  onActiveSortUpdated (activeSort) {
    this.state.sort.forEach(sort => {
      sort.selected = sort.field === activeSort.field && sort.order === activeSort.order
    })
  }

  onPaginationChanged (cursor) {
    this.state.lastInteraction = 'pagination'
    this.state.pagination.from = Number(cursor.current)
    if (cursor.direction === 'next') this.state.pagination.currentPage += 1
    else if (cursor.direction === 'previous') {
      this.state.pagination.currentPage -= 1
    }
    this.syncUrlAndFetch(cursor.direction)
  }

  onPaginationNext () {
    this.state.lastInteraction = 'pagination'
    this.state.pagination.from += this.state.pagination.size
    this.state.pagination.currentPage += 1
    this.syncUrlAndFetch('next')
  }

  initializeStateFromUrl () {
    const searchParams = new URLSearchParams(window.location.search)
    const activeFacets = []

    searchParams.forEach((value, key) => {
      const values = value.includes(',') ? value.split(',') : [value]

      switch (key) {
        case 'sort': {
          const [field, order] = values[0].split('~')
          this.updateActiveSort({ field, order })
          break
        }
        case 'page': {
          const currentPage = parseInt(value, 10)
          this.state.pagination.currentPage = currentPage
          this.state.pagination.from = (currentPage - 1) * this.state.productsPerPage
          break
        }
        case 'q':
          this.state.activeTerm = value
          break
        default: {
          const isFacetField =
            key === 'price' || key === 'available' || key.includes('customFields.')
          if (isFacetField) activeFacets.push({ field: key, value: values })
        }
      }
    })

    this.updateActiveFacets(activeFacets)
  }

  buildSearchParams () {
    const params = new URLSearchParams()

    if (this.state.activeTerm) {
      params.set('q', this.state.activeTerm)
    }

    this.state.activeFacets.forEach(facet => {
      if (facet.value.length > 0) {
        params.set(facet.field, facet.value.join(','))
      }
    })

    if (this.state.activeSort) {
      params.set('sort', `${this.state.activeSort.field}~${this.state.activeSort.order}`)
    }

    if (this.state.pagination.currentPage > 1) {
      params.set('page', this.state.pagination.currentPage)
    }

    return params
  }

  syncUrlAndFetch (direction, { debounceMs = 0 } = {}) {
    const runSync = () => {
      const searchParams = this.buildSearchParams()
      const query = searchParams.toString()
      const url = query ? `${window.location.pathname}?${query}` : window.location.pathname
      window.history.replaceState({}, '', url)
      this.eventBus.publish(EVENTS.API.FETCH_PRODUCTS, {
        targetPage: this.state.pagination.currentPage,
        direction,
      })
    }

    clearTimeout(this.syncTimeout)

    if (debounceMs > 0) {
      this.syncTimeout = setTimeout(runSync, debounceMs)
      return
    }

    runSync()
  }

  updateProducts (products) {
    this.state.products = products
    this.eventBus.publish(EVENTS.STATE.PRODUCTS_UPDATED, {
      products,
      interaction: this.state.lastInteraction || 'initial',
    })
    this.state.lastInteraction = null
    this.eventBus.publish(EVENTS.STATE.SORT_READY, this.state.sort)
  }

  updateFacets (facets) {
    this.state.facets = facets
    this.eventBus.publish(EVENTS.STATE.FACETS_UPDATED, {
      facets,
      activeFacets: this.state.activeFacets,
    })
  }

  updateActiveFacets (activeFacets) {
    this.state.activeFacets = activeFacets
    this.eventBus.publish(EVENTS.STATE.ACTIVE_FACETS_UPDATED, { activeFacets })
  }

  updateActiveSort (activeSort) {
    this.state.activeSort = activeSort
    this.eventBus.publish(EVENTS.STATE.ACTIVE_SORT_UPDATED, activeSort)
  }

  updatePagination (pagination) {
    this.state.pagination = pagination
    this.eventBus.publish(EVENTS.STATE.PAGINATION_UPDATED, pagination)
  }

  get activeTerm () {
    const searchParams = new URLSearchParams(window.location.search)
    return searchParams.get('q') || ''
  }
}

window.theme.results.stateLayer = new ResultsStateLayer()
